/**
 * Class attendance: roster from enrollments, logs & save via Teachers API.
 */

import { createAuthenticatedApi } from "@/lib/utils";
import { toCivilYmd } from "@/lib/utils/date/date-only";
import { addDaysToYmd, studioTodayYmd } from "@/lib/utils/date/studio-clock";
import { occupiesClassSpot } from "@/lib/utils/enrollment";
import {
  AttendanceLogsApi,
  ClassEnrollmentsApi,
  ResponseError,
  TeachersApi,
} from "@/openapi/generated";
import {
  AttendanceStatus,
  type GetAttendanceLogRequest,
  type GetAttendanceLogResponse,
  type GetEnrollmentResponse,
} from "@/openapi/generated/models";

export interface RosterStudent {
  studentId: number;
  firstName: string;
  lastName: string;
}

export interface AttendanceRecord {
  studentId: number;
  date: string;
  status: AttendanceStatus;
  timeIn: string | null;
  timeOut: string | null;
  /** Latest log id for this student on this date (from GET attendance log). */
  attendanceLogId?: string;
  /** Log `createdAt` — API has no modifiedAt, so updates may keep the original time. */
  savedAt?: Date | null;
}

/** One row submitted to LogAttendance (POST). */
export interface AttendanceSavePayload {
  studentId: number;
  status: AttendanceStatus;
  timeIn: string | null;
  timeOut: string | null;
}

function normalizeAttendanceTimeForCompare(
  t: string | null | undefined,
): string {
  if (t == null || t === "") return "";
  const s = String(t).trim();
  return s.length >= 5 ? s.slice(0, 5) : s;
}

/** Same comparison used by save partitioning - UI can show “saved” vs “unsaved changes”. */
export function attendanceRecordMatchesSavePayload(
  server: AttendanceRecord,
  payload: AttendanceSavePayload,
): boolean {
  return (
    server.status === payload.status &&
    normalizeAttendanceTimeForCompare(server.timeIn) ===
      normalizeAttendanceTimeForCompare(payload.timeIn) &&
    normalizeAttendanceTimeForCompare(server.timeOut) ===
      normalizeAttendanceTimeForCompare(payload.timeOut)
  );
}

/** Compare two save payloads (status + times) with the same normalization as the API partitioner. */
export function attendanceSavePayloadsEqual(
  a: AttendanceSavePayload,
  b: AttendanceSavePayload,
): boolean {
  return (
    a.status === b.status &&
    normalizeAttendanceTimeForCompare(a.timeIn) ===
      normalizeAttendanceTimeForCompare(b.timeIn) &&
    normalizeAttendanceTimeForCompare(a.timeOut) ===
      normalizeAttendanceTimeForCompare(b.timeOut)
  );
}

export interface AttendanceSavePartition {
  toCreate: AttendanceSavePayload[];
  toUpdate: Array<{
    attendanceLogId: string;
    payload: AttendanceSavePayload;
  }>;
  unchangedCount: number;
}

/**
 * Splits save payloads: unchanged rows are skipped; new logs use POST;
 * changed rows with a known log id use PUT.
 */
export function partitionAttendanceSaveRequests(
  updates: AttendanceSavePayload[],
  existingForDate: AttendanceRecord[],
): AttendanceSavePartition {
  const byStudent = new Map<number, AttendanceRecord>(
    existingForDate.map((r) => [r.studentId, r]),
  );
  const toCreate: AttendanceSavePayload[] = [];
  const toUpdate: AttendanceSavePartition["toUpdate"] = [];
  let unchangedCount = 0;

  for (const u of updates) {
    const ex = byStudent.get(u.studentId);
    if (!ex) {
      toCreate.push(u);
      continue;
    }
    if (attendanceRecordMatchesSavePayload(ex, u)) {
      unchangedCount += 1;
      continue;
    }
    if (ex.attendanceLogId) {
      toUpdate.push({ attendanceLogId: ex.attendanceLogId, payload: u });
    } else {
      toCreate.push(u);
    }
  }
  return { toCreate, toUpdate, unchangedCount };
}

function logAttendanceApiErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "message" in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === "string" && m.trim()) return m.trim();
  }
  return "Failed to save attendance";
}

async function attendanceMutationErrorMessage(error: unknown): Promise<string> {
  if (error instanceof ResponseError) {
    const { status } = error.response;
    try {
      const text = await error.response.clone().text();
      if (text) {
        try {
          const j = JSON.parse(text) as { message?: string; title?: string };
          if (typeof j.message === "string" && j.message.trim()) {
            return j.message.trim();
          }
        } catch {
          /* not JSON */
        }
        const trimmed = text.trim();
        if (trimmed.length > 0 && trimmed.length < 240) return trimmed;
      }
    } catch {
      /* ignore body read errors */
    }
    if (status === 400) return "Invalid attendance data (400).";
    if (status === 404) return "Attendance record was not found (404).";
    if (status === 409)
      return "This action conflicts with the current record (409). Try refreshing.";
    return `Request failed (${status})`;
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }
  return "Attendance request failed";
}

/** Match member registration: future-dropped enrollments still attend until drop date. */
function enrollmentBelongsOnRoster(e: GetEnrollmentResponse): boolean {
  return occupiesClassSpot(e);
}

const ATTENDANCE_PAGE_SIZE = 200;

function noonLocal(dateStr: string): Date {
  return new Date(`${dateStr}T12:00:00`);
}

/**
 * Same calendar-day encoding as {@link LogAttendanceRequestToJSON} for `date`:
 * `YYYY-MM-DD` only (no time / timezone suffix in the JSON string).
 */
function attendanceLogDateToJsonDay(d: Date): string {
  return toCivilYmd(d) ?? studioTodayYmd(d);
}

function logDateToYmd(d: Date): string {
  return toCivilYmd(d) ?? studioTodayYmd(d);
}

/** Latest log per student (by createdAt). */
function mergeLogsToAttendanceRecords(
  logs: GetAttendanceLogResponse[],
): AttendanceRecord[] {
  const sorted = [...logs].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  const seen = new Set<number>();
  const out: AttendanceRecord[] = [];
  for (const r of sorted) {
    const studentId = Number(r.studentId);
    if (seen.has(studentId)) continue;
    seen.add(studentId);
    const date =
      r.date instanceof Date
        ? logDateToYmd(r.date)
        : String(r.date).slice(0, 10);
    out.push({
      studentId,
      date,
      status: r.status,
      timeIn: r.timeIn,
      timeOut: r.timeOut,
      attendanceLogId: r.id,
      savedAt:
        r.createdAt instanceof Date ? r.createdAt : new Date(r.createdAt),
    });
  }
  return out;
}

async function fetchAttendanceLogPages(
  accessToken: string,
  body: GetAttendanceLogRequest,
): Promise<GetAttendanceLogResponse[]> {
  const api = createAuthenticatedApi(TeachersApi, accessToken);
  const requestBody: GetAttendanceLogRequest = {
    ...body,
    fromDate:
      body.fromDate instanceof Date
        ? attendanceLogDateToJsonDay(body.fromDate)
        : body.fromDate,
    toDate:
      body.toDate instanceof Date
        ? attendanceLogDateToJsonDay(body.toDate)
        : body.toDate,
  };
  const all: GetAttendanceLogResponse[] = [];
  let page = 1;
  let hasMore = true;
  while (hasMore) {
    const res = await api.apiTeachersAttendanceLogPost({
      getAttendanceLogRequest: requestBody,
      page,
      pageSize: ATTENDANCE_PAGE_SIZE,
    });
    const items = res.items ?? [];
    all.push(...items);
    hasMore = res.hasNextPage === true && items.length > 0;
    page += 1;
    if (page > 50) break;
  }
  return all;
}

async function fetchAttendanceLogs(
  accessToken: string,
  teacherId: number,
  classId: number,
  fromDate: Date,
  toDate: Date,
): Promise<GetAttendanceLogResponse[]> {
  const body: GetAttendanceLogRequest = {
    teacherId: teacherId as GetAttendanceLogRequest["teacherId"],
    classId: classId as GetAttendanceLogRequest["classId"],
    studentId: null,
    userId: null,
    status: null,
    fromDate,
    toDate,
  };
  const logs = await fetchAttendanceLogPages(accessToken, body);
  return filterAttendanceLogsForClass(logs, classId);
}

export interface MemberAttendanceAggregate {
  percentage: number;
  attended: number;
  total: number;
  missed: number;
}

/** Backend may return unscoped logs for member calls; keep only the requested student. */
export function filterAttendanceLogsForStudent(
  logs: GetAttendanceLogResponse[],
  studentId: number,
): GetAttendanceLogResponse[] {
  return logs.filter((r) => Number(r.studentId) === studentId);
}

/** Backend may return other classes for the same teacher; keep only the requested class. */
export function filterAttendanceLogsForClass(
  logs: GetAttendanceLogResponse[],
  classId: number,
): GetAttendanceLogResponse[] {
  return logs.filter((r) => Number(r.classId) === classId);
}

/**
 * Roll up attendance log rows for parent/member dashboards.
 * Present + Late count as attended; Absent as missed.
 */
export function aggregateMemberAttendanceStats(
  logs: GetAttendanceLogResponse[],
): MemberAttendanceAggregate {
  let attended = 0;
  let missed = 0;
  for (const r of logs) {
    if (r.status === AttendanceStatus.Absent) missed += 1;
    else attended += 1;
  }
  const total = attended + missed;
  return {
    attended,
    missed,
    total,
    percentage: total > 0 ? Math.round((100 * attended) / total) : 0,
  };
}

/**
 * Attendance log rows for one student (all classes), date range.
 * Uses same Teachers API as teacher roster; member/parent access depends on backend policy.
 */
export async function fetchAttendanceLogsForStudent(
  accessToken: string,
  studentId: number,
  fromDate: Date,
  toDate: Date,
): Promise<GetAttendanceLogResponse[]> {
  const body: GetAttendanceLogRequest = {
    teacherId: null,
    classId: null,
    studentId: studentId as GetAttendanceLogRequest["studentId"],
    userId: null,
    status: null,
    fromDate,
    toDate,
  };
  const logs = await fetchAttendanceLogPages(accessToken, body);
  return filterAttendanceLogsForStudent(logs, studentId);
}

/**
 * Attendance logs for one student in a single class (same date range as roster API).
 */
export async function fetchAttendanceLogsForStudentInClass(
  accessToken: string,
  studentId: number,
  classId: number,
  fromDate: Date,
  toDate: Date,
): Promise<GetAttendanceLogResponse[]> {
  const body: GetAttendanceLogRequest = {
    teacherId: null,
    classId: classId as GetAttendanceLogRequest["classId"],
    studentId: studentId as GetAttendanceLogRequest["studentId"],
    userId: null,
    status: null,
    fromDate,
    toDate,
  };
  const logs = await fetchAttendanceLogPages(accessToken, body);
  return filterAttendanceLogsForClass(
    filterAttendanceLogsForStudent(logs, studentId),
    classId,
  );
}

/** Studio-wide attendance logs for admin dashboard (unscoped request). */
export async function fetchStudioAttendanceLogs(
  accessToken: string,
  fromDate: Date,
  toDate: Date,
): Promise<GetAttendanceLogResponse[]> {
  const body: GetAttendanceLogRequest = {
    teacherId: null,
    classId: null,
    studentId: null,
    userId: null,
    status: null,
    fromDate,
    toDate,
  };
  return fetchAttendanceLogPages(accessToken, body);
}

const ATTENDANCE_LOG_BULK_MAX_STUDENT_IDS = 50;

function chunkIds(ids: number[], size: number): number[][] {
  const chunks: number[][] = [];
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size));
  }
  return chunks;
}

/** One attendance-log call (chunked at 50) for all children in the date range. */
export async function fetchAttendanceLogsForStudents(
  accessToken: string,
  studentIds: number[],
  fromDate: Date,
  toDate: Date,
): Promise<GetAttendanceLogResponse[]> {
  const unique = [
    ...new Set(studentIds.filter((id) => Number.isFinite(id) && id > 0)),
  ];
  if (unique.length === 0) return [];

  const lists = await Promise.all(
    chunkIds(unique, ATTENDANCE_LOG_BULK_MAX_STUDENT_IDS).map((chunk) => {
      const body: GetAttendanceLogRequest = {
        teacherId: null,
        classId: null,
        studentId: null,
        studentIds: chunk,
        userId: null,
        status: null,
        fromDate,
        toDate,
      };
      return fetchAttendanceLogPages(accessToken, body);
    }),
  );
  return lists.flat();
}

async function fetchAllEnrollmentsForClass(
  accessToken: string,
  classId: number,
): Promise<GetEnrollmentResponse[]> {
  const api = createAuthenticatedApi(ClassEnrollmentsApi, accessToken);
  const out: GetEnrollmentResponse[] = [];
  let page = 1;
  let hasMore = true;
  while (hasMore) {
    const res = await api.apiClassEnrollmentsClassesIdGet({
      id: classId,
      page,
      pageSize: ATTENDANCE_PAGE_SIZE,
    });
    const items = res.items ?? [];
    out.push(...items);
    hasMore = res.hasNextPage === true && items.length > 0;
    page += 1;
    if (page > 50) break;
  }
  return out;
}

export async function getRoster(
  accessToken: string,
  classId: number,
): Promise<RosterStudent[]> {
  const enrollments = await fetchAllEnrollmentsForClass(accessToken, classId);
  const seen = new Set<number>();
  const roster: RosterStudent[] = [];
  for (const e of enrollments) {
    if (!enrollmentBelongsOnRoster(e)) continue;
    const sid = Number(e.student.studentId);
    if (seen.has(sid)) continue;
    seen.add(sid);
    roster.push({
      studentId: sid,
      firstName: e.student.firstName,
      lastName: e.student.lastName,
    });
  }
  roster.sort((a, b) =>
    `${a.lastName} ${a.firstName}`.localeCompare(
      `${b.lastName} ${b.firstName}`,
    ),
  );
  return roster;
}

export async function getAttendance(
  accessToken: string,
  teacherId: number,
  classId: number,
  date: string,
): Promise<AttendanceRecord[]> {
  const d = noonLocal(date);
  const logs = await fetchAttendanceLogs(accessToken, teacherId, classId, d, d);
  const forDay = logs.filter((r) => {
    const ymd =
      r.date instanceof Date
        ? logDateToYmd(r.date)
        : String(r.date).slice(0, 10);
    return ymd === date;
  });
  return mergeLogsToAttendanceRecords(forDay);
}

function payloadToApiTimes(u: AttendanceSavePayload): {
  timeIn: string | null;
  timeOut: string | null;
} {
  const needsTimes =
    u.status === AttendanceStatus.Present || u.status === AttendanceStatus.Late;
  return {
    timeIn: needsTimes ? u.timeIn : null,
    timeOut: needsTimes ? u.timeOut : null,
  };
}

/**
 * Creates new logs (POST) and updates existing logs (PUT). Caller should
 * partition with {@link partitionAttendanceSaveRequests} first.
 */
export async function saveAttendance(
  accessToken: string,
  teacherId: number,
  classId: number,
  date: string,
  partition: Pick<AttendanceSavePartition, "toCreate" | "toUpdate">,
): Promise<void> {
  const api = createAuthenticatedApi(TeachersApi, accessToken);
  const classDate = noonLocal(date);

  for (const { attendanceLogId, payload } of partition.toUpdate) {
    const { timeIn, timeOut } = payloadToApiTimes(payload);
    try {
      await api.apiTeachersTeacherIdLogAttendanceIdPut({
        teacherId,
        id: attendanceLogId,
        updateAttendanceLogRequest: {
          status: payload.status,
          timeIn,
          timeOut,
          remarks: null,
        },
      });
    } catch (e) {
      throw new Error(await attendanceMutationErrorMessage(e));
    }
  }

  for (const u of partition.toCreate) {
    const { timeIn, timeOut } = payloadToApiTimes(u);
    const res = await api.apiTeachersIdLogAttendancePost({
      id: teacherId,
      logAttendanceRequest: {
        studentId: u.studentId,
        classId,
        date: classDate,
        status: u.status,
        timeIn,
        timeOut,
        remarks: null,
        appointmentId: null,
      },
    });
    if (!res.isSuccess) {
      throw new Error(logAttendanceApiErrorMessage(res.error));
    }
  }
}

export async function deleteAttendanceLog(
  accessToken: string,
  teacherId: number,
  attendanceLogId: string,
): Promise<void> {
  const api = createAuthenticatedApi(TeachersApi, accessToken);
  try {
    await api.apiTeachersTeacherIdAttendanceLogIdDelete({
      teacherId,
      id: attendanceLogId,
    });
  } catch (e) {
    throw new Error(await attendanceMutationErrorMessage(e));
  }
}

/** Admin attendance page — does not require the class teacher id in the URL. */
export async function deleteAttendanceLogAsAdmin(
  accessToken: string,
  attendanceLogId: string,
): Promise<void> {
  const api = createAuthenticatedApi(AttendanceLogsApi, accessToken);
  try {
    await api.apiAttendanceLogsIdDelete({ id: attendanceLogId });
  } catch (e) {
    throw new Error(await attendanceMutationErrorMessage(e));
  }
}

/** Filter attendance history to last ~60 days for display. */
export function filterAbsencesLast60Days(
  records: AttendanceRecord[],
): AttendanceRecord[] {
  const cutoffStr = addDaysToYmd(studioTodayYmd(), -60);
  return records.filter(
    (r) => r.date >= cutoffStr && r.status === AttendanceStatus.Absent,
  );
}

export async function getAttendanceHistoryForClass(
  accessToken: string,
  teacherId: number,
  classId: number,
): Promise<AttendanceRecord[]> {
  const toYmd = studioTodayYmd();
  const fromYmd = addDaysToYmd(toYmd, -60);
  const toDate = new Date(`${toYmd}T00:00:00.000Z`);
  const fromDate = new Date(`${fromYmd}T00:00:00.000Z`);

  const logs = await fetchAttendanceLogs(
    accessToken,
    teacherId,
    classId,
    fromDate,
    toDate,
  );

  const records: AttendanceRecord[] = logs
    .filter((r) => r.status === AttendanceStatus.Absent)
    .map((r) => ({
      studentId: Number(r.studentId),
      date:
        r.date instanceof Date
          ? logDateToYmd(r.date)
          : String(r.date).slice(0, 10),
      status: AttendanceStatus.Absent,
      timeIn: r.timeIn,
      timeOut: r.timeOut,
    }));

  return records;
}
